import Wizard, {WizardRef} from 'react-native-wizard';
import CampAddress from './CampAddress';
import CampCategory from './CampCategory';
import CampGeneralInformation from './CampGeneralInformation';
import React, {useContext, useEffect, useRef, useState} from 'react'
import {useNavigate} from "react-router-dom";
import {Pressable, StyleSheet, Text, View} from 'react-native';
import StepIndicator from "../common/StepIndicator";
import {v4 as uuidv4} from 'uuid';
import {getRandomBytes} from "expo-crypto";
import DataOps from "../../../../data/DataOps";
import CampImages from "./CampImages";
import CampLicense from "./CampLicense";
import {GlobalStateContext} from "../../../../components/general/GlobalStateProvider";
import {toast} from "react-toastify";
import CampGeoHelper from "../../../../utility/CampGeoHelper";
import { validatePhoneNumber } from "../../../../sms/utils/phoneValidation";

interface Camp {
    campImages?: { url: string }[];
    campLicenses?: { url: string }[];
    campAddress?: string;
    campCategory?: string;
    campName?: string;
    campPhone?: string;
    contactEmail?: string;
    campDescription?: string;
}

const WizardScreen = () => {
    const {user: user, setUser: setUser, isLoadingUser, setShowLoginModal} = useContext(GlobalStateContext);

    const navigation = useNavigate();

    const wizard = useRef<WizardRef>(null);

    const [isFirstStep, setIsFirstStep] = useState<boolean>(false)
    const [isLastStep, setIsLastStep] = useState<boolean>(false)
    const [currentStep, setCurrentStep] = useState(0)
    const [isUploading, setUploading] = useState(false);

    const [camp, setCamp] = useState<Camp>({});
    const [warningMessage, setWarningMessage] = useState("");

    useEffect(() => {
        if (isLoadingUser) return;

        if (!user) {
            setShowLoginModal(true);
            navigation('/hosting-create-camp');
            return;
        }

        if (user.accountType !== 'host') {
            toast.error('You must be signed in with a host account to create a camp.');
            navigation('/hosting-create-camp');
            return;
        }

        if (Array.isArray((user as any).camps) && (user as any).camps.length > 0) {
            toast.error('Only one SnapCamp can be created per account.');
            navigation('/hosting-manage-camp');
        }
    }, [user, isLoadingUser, navigation, setShowLoginModal]);

    const stepList = [
        {
            content: <CampLicense camp={camp} setCamp={setCamp}/>,
        },
        {
            content: <CampAddress camp={camp} setCamp={setCamp}/>,
        },
        {
            content: <CampCategory camp={camp} setCamp={setCamp}/>
        },
        {
            content: <CampGeneralInformation camp={camp} setCamp={setCamp}/>
        },
        {
            content: <CampImages camp={camp} setCamp={setCamp}/>
        },
    ]

    const uploadCampImages = async () => {
        try {
            const imageUrls = [];
            for (const image of camp.campImages) {
                const imageId = uuidv4({random: getRandomBytes(16)});
                const imageUrl = await DataOps.uploadImage(image.url, imageId);
                imageUrls.push(imageUrl);
            }
            return imageUrls;
        } catch (error) {
            console.error("Error uploading images, ", error);
            toast.error('Uh oh, an error occurred while creating your snap camp.');
        }
    }

    const uploadCampFiles = async () => {
        try {
            const fileUrls = [];
            for (const license of camp.campLicenses) {
                const fileId = uuidv4({random: getRandomBytes(16)});
                const fileUrl = await DataOps.uploadImage(license.url, fileId);
                fileUrls.push(fileUrl);
            }
            return fileUrls;
        } catch (error) {
            console.error("Error uploading files, ", error);
            toast.error('Uh oh, an error occurred while creating your snap camp.');
        }
    }

    const uploadCamp = async (imageUrls, fileUrls) => {
        try {
            const campId = uuidv4({random: getRandomBytes(16)});
            const isLocalhostCamp = typeof window !== 'undefined'
                && ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
            const campEnvironment = isLocalhostCamp ? 'test' : 'prod';
            let updatedCamp = {
                ...camp,
                campImageUrls: imageUrls,
                campFileUrls: fileUrls,
                campId: campId,
                environment: campEnvironment,
                isTestCamp: campEnvironment === 'test',
                isProdCamp: campEnvironment === 'prod',
                updatedAt: new Date().toISOString(),
            };
            
            // Add geolocation for efficient spatial search
            updatedCamp = await CampGeoHelper.enrichCampWithGeolocation(updatedCamp);
            
            await DataOps.saveCustomDataToFirebase('camp', campId, updatedCamp, true)
            return campId;
        } catch (error) {
            console.error("Error uploading camp, ", error);
            toast.error('Uh oh, an error occurred while creating your snap camp.');
        }
    }

    const updateUser = async (campId) => {
        try {
            const updatedUser = {
                ...user,
                camps: [...user.camps, campId],
            }
            setUser(prevState => ({
                ...prevState,
                campCamps: [...user.camps, campId],
            }));
            await DataOps.saveCustomDataToFirebase('user', user.userId, updatedUser, true);
        } catch (error) {
            console.error("Error updating user, ", error);
            toast.error('Uh oh, an error occurred while creating your snap camp.');
        }
    }

    const onSubmit = async () => {
        setUploading(true);

        const imageUrls = await uploadCampImages();
        const fileUrls = await uploadCampFiles();
        const campId = await uploadCamp(imageUrls, fileUrls);
        await updateUser(campId);

        setUploading(false);

        navigation('/hosting-manage-camp');

        toast.success('SnapCamp successfully created!');
    }

    return (
        <View style={{flex: 1, height: '100%'}}>
            <View style={{flexDirection: "column", height: '85%'}}>
                <Wizard
                    ref={wizard}
                    steps={stepList}
                    isFirstStep={val => setIsFirstStep(val)}
                    isLastStep={val => setIsLastStep(val)}
                    onNext={() => {
                    }}
                    currentStep={({currentStep}) => {
                        setCurrentStep(currentStep)
                    }}
                />
            </View>

            <StepIndicator currentStep={currentStep} totalSteps={stepList.length}/>

            <View style={{flexDirection: 'row', alignItems: 'center'}}>
                <Pressable style={[styles.bottomButtons, {marginLeft: 50}]}
                           onPress={() => {
                               if (isFirstStep) {
                                   navigation('/hosting-create-camp');
                               } else {
                                   wizard.current.prev();
                               }
                           }}>
                    <Text style={styles.bottomText}>Back</Text>
                </Pressable>

                <View style={{flex: 1}}/>

                <Text>{warningMessage}</Text>
                <Pressable style={[styles.bottomButtons, {marginRight: 50}]}
                           onPress={() => {
                               if (isLastStep) {
                                   onSubmit();
                               } else {
                                   if (currentStep === 0 && !('campLicenses' in camp)) {
                                       setWarningMessage('*Please upload valid permits/licenses to continue.     ');
                                       return;
                                   } else if (currentStep === 1 && !('campAddress' in camp)) {
                                       setWarningMessage('*A valid address is needed for camp registration.     ');
                                       return;
                                   } else if (currentStep === 2 && !('campCategory' in camp)) {
                                       setWarningMessage('*At least one camp category should be selected.     ')
                                       return;
                                   } else if (
                                       currentStep === 3
                                       && (
                                           !('campName' in camp) ||
                                           !('campPhone' in camp) ||
                                           !('contactEmail' in camp) ||
                                           !('campDescription' in camp)
                                       )
                                   ) {
                                       setWarningMessage('*Please fill out all required fields.     ')
                                       return;
                                   } else if (currentStep === 3) {
                                       const phone = String(camp.campPhone || '');
                                       const phoneValidation = validatePhoneNumber(phone);
                                       if (!phoneValidation.ok) {
                                           setWarningMessage(`*${phoneValidation.msg}     `);
                                           return;
                                       }
                                   }

                                   wizard.current.next();
                                   setWarningMessage('');
                               }
                           }}>
                    <Text style={styles.bottomText}>
                        {
                            isLastStep
                                ?
                                isUploading
                                    ? 'Creating SnapCamp...'
                                    : 'Complete'
                                : 'Next'
                        }
                    </Text>
                </Pressable>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    bottomButtons: {
        backgroundColor: 'black',
        paddingHorizontal: 20,
        paddingVertical: 13,
        borderRadius: 5,
        width: '15%',
        alignItems: 'center',
        justifyContent: 'center',
    },
    bottomText: {
        color: 'white', fontSize: 18
    },
    warningText: {
        color: 'red',
    },
});

export default WizardScreen;
